import useAxios from 'axios-hooks';
import React, { useState } from 'react';
import * as yup from 'yup';
import {
  VisitResponse,
  MasterInfo,
  ServiceGroupsInfo,
  FormValues,
  SpecialistCases,
} from 'types';
import { Loader, Wizard, WizardPage } from 'components';
import { AxiosError } from 'axios';
import { get } from 'lodash-es';
import { EXTERNAL_PROVIDERS } from 'constants/externalProviders';
import { ContactsEvent } from './Contacts';
import { useSearchParams } from '../../hooks';
import { GroupEvent } from './GroupEvent';

const phoneErrorMessage = 'Введите номер телефона в формате +7(XXX) XXX-XX-XX';

const contactsValidationSchema = yup.object().shape({
  clientInfo: yup.object().shape({
    name: yup.string().required('Введите имя'),
    phone: yup
      .string()
      .matches(/^(\+)(\d)(\()(\d{3})(\))(\s)(\d{3})(-)(\d{2})(-)(\d{2})$/g, {
        message: phoneErrorMessage,
      })
      .required(phoneErrorMessage),
    personsCount: yup.number().default(1).required('Укажите количество мест'),
    comment: yup.string(),
  }),
});

const eventValidationSchema = yup.object().shape({
  groupEventInfo: yup.object().shape({
    visitId: yup.string().required(),
  }),
});

export const FormPageGroup: React.FC<{
  initialValues: Partial<FormValues>;
  address: string;
  onFinish: (response: VisitResponse) => void;
  onExit: () => void;
  dataLoading: boolean;
  dataError?: AxiosError<unknown>;
  data: {
    mastersData: MasterInfo[];
    servicesData: ServiceGroupsInfo;
    specialistCases: SpecialistCases;
    additionalPersonsAllowed: boolean;
    fieldNameLabel: string;
    fieldCommentLabel: string;
  };
  duration: string;
  divisionId: string | null;
  timezone?: string;
  isBookingFromVk: boolean;
}> = ({
  initialValues,
  address,
  onFinish,
  onExit,
  dataLoading,
  dataError,
  data,
  divisionId,
  timezone,
  isBookingFromVk,
}) => {
  const source =
    useSearchParams('source', EXTERNAL_PROVIDERS) ||
    (isBookingFromVk ? 'vk_system' : null);
  const [postEventError, setPostEventError] = useState(null);
  const resetPostEventError = () => setPostEventError(null);

  const [{ loading: formLoading }, postVisit] = useAxios<VisitResponse>(
    {
      method: 'POST',
      url: '/booking-widget/client/visit-group/book',
    },
    {
      manual: true,
    },
  );

  const loading = formLoading || dataLoading;
  const error = dataError;

  const onSubmit = (values: FormValues) => {
    const { name, phone = '', personsCount, comment } = values.clientInfo;
    return postVisit({
      data: {
        widgetId: values.widgetId,
        visitId: values.groupEventInfo.visitId,
        clientInfo: {
          name,
          phone: phone.replace(/[()\s-]/g, ''),
        },
        personsCount: Number(personsCount) || 1,
        comment,
        ...(divisionId ? { divisionId } : {}),
        ...(source ? { source } : {}),
      },
    })
      .then((res) => {
        const visitId = get(res, 'data.id');
        if (visitId)
          window.history.replaceState(
            null,
            '',
            `?visitId=${visitId}&visitType=group`,
          );
        onFinish(res.data);
      })
      .catch((err: AxiosError<VisitResponse>) => {
        const errData = get(err, 'response.data');
        setPostEventError(errData);
      });
  };

  return (
    <>
      <Loader loading={loading} error={error} />
      <Wizard initialValues={initialValues} onSubmit={onSubmit} onExit={onExit}>
        <WizardPage
          title="Выбор события"
          name="groupEventInfo"
          validationSchema={eventValidationSchema}
        >
          <GroupEvent
            name="groupEventInfo"
            serviceGroups={data.servicesData}
            masters={data.mastersData}
            divisionId={divisionId}
            specialistCases={data.specialistCases}
            timezone={timezone}
          />
        </WizardPage>
        <WizardPage
          title="Подтверждение записи"
          name="clientInfo"
          validationSchema={contactsValidationSchema}
        >
          <ContactsEvent
            widgetId={initialValues.widgetId}
            address={address}
            masters={data.mastersData}
            serviceGroups={data.servicesData}
            specialistCases={data.specialistCases}
            postEventError={postEventError}
            resetPostEventError={resetPostEventError}
            additionalPersonsAllowed={data.additionalPersonsAllowed}
            fieldNameLabel={data.fieldNameLabel}
            fieldCommentLabel={data.fieldCommentLabel}
          />
        </WizardPage>
      </Wizard>
    </>
  );
};
